import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, of, switchMap } from "rxjs";
import { SnackbarService } from "../../core/services";
import { BugReportApi } from "./bug-report.api";
import * as BugReportActions from "./bug-report.actions";

@Injectable()
export class BugReportEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(BugReportApi);
  private readonly snackbar = inject(SnackbarService);

  fetchBugReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BugReportActions.BugReportFetchRequest),
      switchMap(() =>
        this.api.listBugReports().pipe(
          map((data) => BugReportActions.BugReportFetchSuccess({ reports: data.data ?? [] })),
          catchError((error) => of(BugReportActions.BugReportFetchFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  updateBugReportStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BugReportActions.BugReportUpdateStatusRequest),
      switchMap(({ id, status }) =>
        this.api.updateBugReportStatus(id, status).pipe(
          map((data) => BugReportActions.BugReportUpdateStatusSuccess({ report: data.data })),
          catchError((error) => of(BugReportActions.BugReportUpdateStatusFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  createBugReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BugReportActions.BugReportCreateRequest),
      switchMap(({ payload }) =>
        this.api.createBugReport(payload).pipe(
          map((data) => BugReportActions.BugReportCreateSuccess({ report: data.data })),
          catchError((error) => of(BugReportActions.BugReportCreateFailure({ error: error?.message ?? error }))),
        ),
      ),
    ),
  );

  fetchBugReportsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BugReportActions.BugReportFetchFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to load bug reports: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );

  createBugReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BugReportActions.BugReportCreateSuccess),
        map(() => {
          this.snackbar.showNotification('success', '', 'Bug report submitted. Thank you!', 'Close');
        }),
      ),
    { dispatch: false },
  );

  createBugReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BugReportActions.BugReportCreateFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to submit bug report: ${error}`, 'Close');
        }),
    ),
    { dispatch: false },
  );

  updateBugReportStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BugReportActions.BugReportUpdateStatusSuccess),
        map(({ report }) => {
          const message = report.status === 'closed'
            ? 'Bug report closed successfully'
            : 'Bug report reopened successfully';
          this.snackbar.showNotification('success', '', message, 'Close');
        }),
      ),
    { dispatch: false },
  );

  updateBugReportStatusFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BugReportActions.BugReportUpdateStatusFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to update bug report: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );
}
